import { Chip, Stack } from '@mui/material';
import { t } from 'i18next';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { CallSiteStatus, getCallSiteStatus } from '../enums/CallSiteStatus';
import { ReportCallSite } from '../models/ReportCallSite';
import { ReportCallsSitesService } from '../services/ReportCallSitesService';
import { DocsAlert } from '../../../common/docs/DocsAlert';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { EXTERNAL_URLS } from '../../../routing/routes';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {};

export class Controller extends PaginatedTableController<ReportCallSite, Props, State> {
  private readonly callSitesService = new ReportCallsSitesService();

  componentDidMount(): void {
    super.componentDidMount();
    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);
    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.callSitesService.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ReportCallSite>> {
    const { report } = this.props.context;
    if (!(report instanceof ReportDone)) return BLANK_PAGINATION_RESULT;

    try {
      return await this.callSitesService.getList(report.getId(), cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected renderTable(tableComponent: React.ReactElement) {
    const { initialDataLoad } = this.state;

    return (
      <Stack gap={2}>
        {!initialDataLoad && <DocsAlert helpText="callSitesGuidance" href={UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_CALL_SITE_STATUS)} />}
        {tableComponent}
      </Stack>
    );
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'callingClassMethod',
        label: t('callingMethod'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'location',
        label: t('location'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'calledClassMethod',
        label: t('calledMethod'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'status',
        label: t('status'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: CallSiteStatus.MISSED, label: t('missed') },
          { value: CallSiteStatus.COVERED, label: t('covered') },
          { value: CallSiteStatus.NOT_COVERED, label: t('notCovered') },
        ],
      },
    ];
  }

  protected getFieldConfigurations(): FieldConfiguration<ReportCallSite>[] {
    return [
      {
        fieldKey: 'callingMethod',
        sortable: true,
        gqlKeys: { sorterField: 'callingClassMethod' },
        label: t('callingMethod'),
        description: t('callingMethod'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getCallingClassMethod()}</TypographyOverflow>,
      },
      {
        fieldKey: 'location',
        sortable: true,
        gqlKeys: { sorterField: 'location' },
        label: t('location'),
        description: t('location'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getLocation()}</TypographyOverflow>,
      },
      {
        fieldKey: 'calledMethod',
        sortable: true,
        gqlKeys: { sorterField: 'calledClassMethod' },
        label: t('calledMethod'),
        description: t('calledMethod'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getCalledClassMethod()}</TypographyOverflow>,
      },
      {
        fieldKey: 'status',
        sortable: true,
        label: t('status'),
        columnProps: { maxWidth: 300 },
        description: t('status'),
        render: (item) => <Chip label={getCallSiteStatus(item.getCovered())} />,
      },
    ];
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the call sites
      super.doInitialDataFetch();
    }
  }
}

export const ReportCallSitesController = withRouter<ReportOutletContextType>(Controller);
